import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Eye, EyeSlash, ArrowRight, Envelope, Lock } from '@phosphor-icons/react';

const API = process.env.REACT_APP_BACKEND_URL;
const DEFAULT_STORE_SLUG = "mrg";

const StoreLoginPage = ({ storeSlug: propStoreSlug }) => {
  const { storeSlug: paramStoreSlug } = useParams();
  const storeSlug = propStoreSlug || paramStoreSlug || DEFAULT_STORE_SLUG;
  const standalone = !!propStoreSlug || !paramStoreSlug;
  const [store, setStore] = useState(null);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [isRegister, setIsRegister] = useState(false);
  const [name, setName] = useState('');
  const [clientType, setClientType] = useState('particulier');
  const [companyName, setCompanyName] = useState('');
  const [siret, setSiret] = useState('');
  const { login, register, user } = useAuth();
  const navigate = useNavigate();

  // Helper for standalone links
  const getLink = (path) => standalone ? path : `/store/${storeSlug}${path}`;

  useEffect(() => {
    fetch(`${API}/api/s/${storeSlug}`)
      .then(r => r.json())
      .then(d => setStore(d))
      .catch(() => {});
  }, [storeSlug]);

  // If already logged in, redirect to account
  useEffect(() => {
    if (user) navigate(getLink('/account'), { replace: true });
  }, [user, navigate, standalone, storeSlug]);

  const id = store?.identity || {};
  const colors = {
    primary: id.primary_color || '#111111',
    accent: id.secondary_color || id.accent_color || '#F9055B',
    dark: '#0e0f12',
  };
  const logoUrl = id.logo_url || id.logo_light || '';
  const isMagere = id.theme_template === 'magere' || storeSlug === 'mrg' || storeSlug === 'magere-viandes' || standalone;
  const heroVideoId = id.hero_video_id || 'JDRcBmcIf98';

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      if (isRegister) {
        await register(name, email, password, 'customer');
        // Save client type profile if pro
        if (clientType === 'pro') {
          try {
            const storeData = await fetch(`${API}/api/s/${storeSlug}`).then(r => r.json());
            if (storeData?.id) {
              await fetch(`${API}/api/stores/${storeData.id}/customers/${encodeURIComponent(email)}/profile`, {
                method: 'PUT', headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
                body: JSON.stringify({ client_type: 'pro', company_name: companyName, siret })
              });
            }
          } catch { /* ignore profile update error */ }
        }
      } else {
        const result = await login(email, password);
        if (!result.success) {
          setError(result.error || 'Identifiants incorrects');
          setLoading(false);
          return;
        }
      }
      navigate(getLink('/account'));
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : err.message || 'Erreur de connexion');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen relative overflow-hidden flex flex-col" style={{ fontFamily: "'Poppins', sans-serif" }}>
      <link href="https://fonts.googleapis.com/css2?family=Jost:wght@300;400;500;600;700;900&family=Dancing+Script:wght@400;700&display=swap" rel="stylesheet" />

      {/* Background : same hero video as homepage (YouTube embed) + black 70% overlay */}
      <div className="absolute inset-0 z-0 overflow-hidden bg-black">
        <iframe
          title="MRG hero background"
          src={`https://www.youtube.com/embed/${heroVideoId}?autoplay=1&mute=1&loop=1&playlist=${heroVideoId}&controls=0&showinfo=0&modestbranding=1&playsinline=1&rel=0&disablekb=1&vq=hd1080&hd=1`}
          className="absolute top-1/2 left-1/2 pointer-events-none border-0"
          style={{ width: '100vw', height: '56.25vw', minWidth: '177.78vh', minHeight: '100vh', transform: 'translate(-50%, -50%)' }}
          allow="autoplay; encrypted-media; picture-in-picture" frameBorder="0"
        />
      </div>
      {/* Black overlay 70% */}
      <div className="absolute inset-0 z-[1] bg-black/70" />

      {/* Header */}
      <header className="relative z-10 py-6 px-6">
        <div className="max-w-[1200px] mx-auto flex items-center justify-between">
          <Link to={getLink('/')} className="flex items-center gap-3">
            <img src="/api/static/branding/mrg-logo-pink.png" alt={store?.name || 'MRG Optique & Audition'} className="h-[70px] w-[70px] object-cover rounded-full shadow-lg ring-2 ring-white/20" />
            <span className="text-white text-[22px] font-bold hidden md:block">{store?.name}</span>
          </Link>
          <Link to={getLink('/')} className="text-white/60 hover:text-white text-[14px] font-medium flex items-center gap-2 transition-colors">
            Retour à la boutique <ArrowRight size={16} />
          </Link>
        </div>
      </header>

      {/* Main content */}
      <div className="relative z-10 flex-1 flex items-center justify-center px-4 py-8">
        <div className="w-full max-w-[440px]">
          <div className="bg-white/10 backdrop-blur-2xl rounded-2xl border border-white/20 shadow-2xl overflow-hidden" data-testid="store-login-card">
            {/* Top accent */}
            <div className="h-[4px]" style={{ backgroundColor: colors.accent }} />

            <div className="p-8 md:p-10">
              <div className="text-center mb-8">
                <h1 className="text-[26px] font-bold text-white mb-2">
                  {isRegister ? 'Créer un compte' : 'Mon espace client'}
                </h1>
                <p className="text-white/50 text-[14px]">
                  {isRegister ? `Inscrivez-vous sur ${store?.name || 'la boutique'}` : `Connectez-vous à votre compte ${store?.name || ''}`}
                </p>
              </div>

              <form onSubmit={handleSubmit} className="space-y-5">
                {error && (
                  <div className="p-3.5 rounded-xl bg-red-500/20 border border-red-500/30 text-red-200 text-[13px]" data-testid="login-error">
                    {error}
                  </div>
                )}

                {isRegister && (
                  <div className="space-y-2">
                    <Label className="text-white/80 text-[13px]">Nom complet</Label>
                    <Input
                      type="text"
                      placeholder="Jean Dupont"
                      value={name}
                      onChange={e => setName(e.target.value)}
                      required
                      className="bg-white/10 border-white/20 text-white placeholder:text-white/30 focus:border-white/40 h-[48px] rounded-xl text-[14px]"
                      data-testid="register-name"
                    />
                  </div>
                )}

                {isRegister && (
                  <div className="space-y-2">
                    <Label className="text-white/80 text-[13px]">Type de compte</Label>
                    <div className="flex gap-2">
                      <button type="button" onClick={() => setClientType('particulier')}
                        className={`flex-1 py-2.5 rounded-xl text-[13px] font-medium border-2 transition-all ${clientType === 'particulier' ? 'text-white border-white/60 bg-white/20' : 'text-white/40 border-white/10 bg-white/5'}`}
                        data-testid="type-particulier">
                        Particulier
                      </button>
                      <button type="button" onClick={() => setClientType('pro')}
                        className={`flex-1 py-2.5 rounded-xl text-[13px] font-medium border-2 transition-all ${clientType === 'pro' ? 'text-white border-white/60 bg-white/20' : 'text-white/40 border-white/10 bg-white/5'}`}
                        data-testid="type-pro">
                        Professionnel
                      </button>
                    </div>
                  </div>
                )}

                {isRegister && clientType === 'pro' && (
                  <div className="space-y-2">
                    <Input type="text" placeholder="Raison sociale" value={companyName}
                      onChange={e => setCompanyName(e.target.value)}
                      className="bg-white/10 border-white/20 text-white placeholder:text-white/30 focus:border-white/40 h-[44px] rounded-xl text-[13px]"
                      data-testid="register-company" />
                    <Input type="text" placeholder="SIRET (optionnel)" value={siret}
                      onChange={e => setSiret(e.target.value)}
                      className="bg-white/10 border-white/20 text-white placeholder:text-white/30 focus:border-white/40 h-[44px] rounded-xl text-[13px]"
                      data-testid="register-siret" />
                  </div>
                )}

                <div className="space-y-2">
                  <Label className="text-white/80 text-[13px]">Adresse email</Label>
                  <div className="relative">
                    <Envelope size={18} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-white/30" />
                    <Input
                      type="email"
                      placeholder="vous@exemple.com"
                      value={email}
                      onChange={e => setEmail(e.target.value)}
                      required
                      className="bg-white/10 border-white/20 text-white placeholder:text-white/30 focus:border-white/40 h-[48px] rounded-xl text-[14px] pl-11"
                      data-testid="store-login-email"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label className="text-white/80 text-[13px]">Mot de passe</Label>
                  <div className="relative">
                    <Lock size={18} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-white/30" />
                    <Input
                      type={showPassword ? 'text' : 'password'}
                      placeholder="••••••••"
                      value={password}
                      onChange={e => setPassword(e.target.value)}
                      required
                      className="bg-white/10 border-white/20 text-white placeholder:text-white/30 focus:border-white/40 h-[48px] rounded-xl text-[14px] pl-11 pr-12"
                      data-testid="store-login-password"
                    />
                    <button type="button" onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-white/40 hover:text-white/70">
                      {showPassword ? <EyeSlash size={18} /> : <Eye size={18} />}
                    </button>
                  </div>
                </div>

                <Button type="submit" disabled={loading}
                  className="w-full h-[50px] rounded-xl text-white text-[15px] font-bold transition-all hover:brightness-110 hover:shadow-xl"
                  style={{ backgroundColor: colors.accent }}
                  data-testid="store-login-submit">
                  {loading ? (
                    <span className="flex items-center gap-2">
                      <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                      {isRegister ? 'Inscription...' : 'Connexion...'}
                    </span>
                  ) : (
                    <span className="flex items-center gap-2 justify-center">
                      {isRegister ? "S'inscrire" : 'Se connecter'} <ArrowRight size={18} weight="bold" />
                    </span>
                  )}
                </Button>
              </form>

              <div className="mt-6 text-center">
                <button onClick={() => { setIsRegister(!isRegister); setError(''); }}
                  className="text-[13px] text-white/70 hover:text-white transition-colors">
                  {isRegister ? 'Déjà un compte ?' : 'Pas encore de compte ?'}{' '}
                  <span className="font-bold" style={{ color: colors.accent }}>
                    {isRegister ? 'Se connecter' : "S'inscrire"}
                  </span>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="relative z-10 py-4 text-center">
        <p className="text-white/40 text-[11px]">{id.company_name || store?.name} &copy; {new Date().getFullYear()}</p>
      </footer>
    </div>
  );
};

export default StoreLoginPage;
